import { UserRegisterInput } from 'dtos/users/UserRegisterInput';
import { makeAutoObservable, runInAction } from 'mobx';
import { registerForm } from 'forms/registerUserForm';
import { Match } from 'navigo';
import { AuthStore } from 'stores/users/AuthStore';
import { LoginOutput } from 'dtos/users/LoginOutput';
import { HttpException, LoadStatus } from 'utils/types';
import { serverUri } from 'utils/config';
import { nullsToUndefined } from 'utils/nullsToUndefined';
import { emailAlreadyExistsMessage, emailIsNotConfirmedMessage } from 'dtos/users/common';
import { SocialAuthStore } from 'stores/users/SocialAuthStore';
import { ConfirmEmailStore } from './ConfirmEmailStore';

export class RegisterUserStore {
  form = registerForm();
  status: LoadStatus | 'errorEmailExists' = 'none';
  message?: string;

  constructor(
    private readonly authStore: AuthStore,
    private readonly socialAuthStore: SocialAuthStore,
    private readonly confirmEmailStore: ConfirmEmailStore
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  activate(match: Match) {
    this.status = 'none';
    this.message = undefined;
    this.form.reset();
    this.socialAuthStore.reset();
  }

  async registerUser() {
    this.status = 'loading';
    this.message = undefined;
    this.socialAuthStore.reset();
    const { isValid } = await this.form.validate({ showErrors: true });
    if (!isValid) {
      return;
    }
    const dto = this.formToDto(this.form.values());
    try {
      const response = await fetch(`${serverUri}/user/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
      });
      if (response.ok) {
        //TODO: consider removing this block or handling email confirmation differently, without throwing an exception.
        // Now it won't ever go into this branch even if registration goes properly,
        // as we throw the same UnconfirmedEmailException as we do on login,
        // so registration is not finished and user is not logged in right after that until email is confirmed.
        const output: LoginOutput = nullsToUndefined(await response.json());
        this.authStore.handleLoginResponse(output);
      } else {
        const json: HttpException = nullsToUndefined(await response.json());
        runInAction(() => {
          if (json && json.message === emailAlreadyExistsMessage) {
            this.status = 'errorEmailExists';
            this.message = 'Email address is already in use';
          } else if (json && json.message === emailIsNotConfirmedMessage) {
            this.confirmEmailStore.resendEmail(dto.email);
          } else {
            this.status = 'error';
            this.message = `Unable to create user.${json.message ? ` ${json.message}` : ''}`;
          }
        });
      }
    } catch (err) {
      runInAction(() => {
        this.status = 'error';
        this.message = `Unable to create user.${err instanceof Error ? ` ${err.message}` : ''}`;
      });
    }
  }

  private formToDto(form: any): UserRegisterInput {
    const { newEmail, password } = form;
    const dto: UserRegisterInput = {
      email: newEmail,
      password,
    };
    return dto;
  }
}
